import {
  type QueryClient,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { getCreditBalance } from '@/api/credits';
import { authClient } from '@/auth/client';
import { m } from '@/locale/paraglide/messages';

interface CreditsBalance {
  balance: number;
}

function userCreditKey(userId: string | null | undefined) {
  return userId ?? 'anonymous';
}

export const creditsKeys = {
  all: ['credits'] as const,
  balance: (userId?: string | null) =>
    [...creditsKeys.all, 'balance', userCreditKey(userId)] as const,
  history: (userId?: string | null) =>
    [...creditsKeys.all, 'history', userCreditKey(userId)] as const,
};

export function invalidateAllCredits(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: creditsKeys.all });
}

async function fetchCreditsBalance(): Promise<CreditsBalance> {
  const response = await fetch('/api/credits/balance', {
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(m.settings_credits_load_failed());
  }

  return response.json();
}

export function formatCreditsBalance(balance: number | undefined) {
  return typeof balance === 'number' ? balance.toLocaleString() : '--';
}

export function useCredits() {
  const { data: session, isPending: isSessionPending } =
    authClient.useSession();
  const userId = session?.user?.id;

  return useQuery({
    queryKey: creditsKeys.balance(userId),
    queryFn: fetchCreditsBalance,
    enabled: typeof window !== 'undefined' && !isSessionPending && !!userId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

export function useCreditHistory() {
  const { data: session, isPending: isSessionPending } =
    authClient.useSession();
  const userId = session?.user?.id;

  return useQuery({
    queryKey: creditsKeys.history(userId),
    queryFn: () => getCreditBalance(),
    enabled: typeof window !== 'undefined' && !isSessionPending && !!userId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useInvalidateCredits() {
  const queryClient = useQueryClient();
  return useCallback(() => invalidateAllCredits(queryClient), [queryClient]);
}
